'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react';
import { useReducedMotion } from 'framer-motion';

type Frame = (t: number) => void;
type Register = (frame: Frame) => () => void;

const PERIOD = 8;
const LEAD_IN = 0.5;

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const LoopContext = createContext<Register | null>(null);

export const seg = (t: number, a: number, b: number) => Math.min(1, Math.max(0, (t - a) / (b - a)));
export const easeOut = (p: number) => 1 - Math.pow(1 - p, 4);
export const easeInOut = (p: number) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
export const backOut = (p: number) => {
  const c = 1.70158;
  return 1 + (c + 1) * Math.pow(p - 1, 3) + c * Math.pow(p - 1, 2);
};

export function setText(el: HTMLElement | null, text: string) {
  const node = el?.firstChild;
  if (node && node.nodeType === Node.TEXT_NODE) {
    if (node.nodeValue !== text) node.nodeValue = text;
  } else if (el && el.textContent !== text) {
    el.textContent = text;
  }
}

export function useLoopFrame(frame: Frame) {
  const register = useContext(LoopContext);
  const latest = useRef(frame);
  latest.current = frame;
  useIsoLayoutEffect(() => register?.((t) => latest.current(t)), [register]);
}

export function StepsLoop({ root, children }: { root: RefObject<HTMLElement | null>; children: ReactNode }) {
  const reduce = useReducedMotion();
  const frames = useRef(new Set<Frame>());

  const register = useCallback<Register>((frame) => {
    frames.current.add(frame);
    frame(0);
    return () => {
      frames.current.delete(frame);
    };
  }, []);

  useEffect(() => {
    const el = root.current;
    if (!el || reduce) return;
    let visible = false;
    let clock = 0;
    let last = 0;
    let raf = 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const viewport = entry.rootBounds?.height ?? window.innerHeight;
        visible = entry.intersectionRatio >= 0.3 || entry.intersectionRect.height >= 0.3 * viewport;
      },
      { threshold: Array.from({ length: 21 }, (_, i) => i / 20) },
    );
    observer.observe(el);

    const tick = (now: number) => {
      if (!visible) {
        last = 0;
      } else {
        if (last) clock += (now - last) / 1000;
        last = now;
        if (clock >= LEAD_IN) {
          const t = (clock - LEAD_IN) % PERIOD;
          frames.current.forEach((frame) => frame(t));
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [root, reduce]);

  return <LoopContext.Provider value={reduce ? null : register}>{children}</LoopContext.Provider>;
}
